import React, { useEffect, useState } from 'react';
import { Box, Link } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  stateAgencyMappingService,
  StateAgencyVerifyUrlResponse,
} from '../services/stateAgencyMappingService';

// FEAT-047 — Careseeker-facing "Verify with [agency]" link (web).
// MVP-8.18 (verify via state agency), MVP-8.35a (visible when number on
// file), MVP-8.35c (unmapped state renders nothing).
//
// Tone: neutral and confidence-building per §1.2 — "Verify with HCSB"
// rather than "Check this caregiver's license". The component renders
// nothing when the state is unmapped or when the caregiver has no
// licenseNumber on file.

export interface StateAgencyVerifyLinkProps {
  stateCode: string | null | undefined;
  licenseNumber: string | null | undefined;
}

export const StateAgencyVerifyLink: React.FC<StateAgencyVerifyLinkProps> = ({
  stateCode,
  licenseNumber,
}) => {
  const [result, setResult] = useState<StateAgencyVerifyUrlResponse | null>(null);
  const [loaded, setLoaded] = useState(false);

  const trimmedLicense = licenseNumber?.trim() ?? '';
  const hasLicense = trimmedLicense.length > 0;

  useEffect(() => {
    let cancelled = false;
    if (!stateCode || !hasLicense) {
      setResult(null);
      setLoaded(true);
      return () => {
        cancelled = true;
      };
    }
    setLoaded(false);
    stateAgencyMappingService
      .getVerifyUrl(stateCode, trimmedLicense)
      .then((value) => {
        if (!cancelled) {
          setResult(value);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult(null);
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [stateCode, trimmedLicense, hasLicense]);

  if (!loaded) return null;
  if (!result) return null;

  return (
    <Box sx={{ mt: 1 }} data-testid="state-agency-verify-link">
      <Link
        href={result.verifyUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        sx={{ fontWeight: 500 }}
        aria-label={`Verify with ${result.agencyLabel} (opens in a new tab)`}
      >
        Verify with {result.agencyLabel}
        <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
      </Link>
    </Box>
  );
};

export default StateAgencyVerifyLink;
